/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：メッセージボックスサービス
 *
 * 作成者：2021/11/19 ノア）黄
 * 最終修正者：2021/11/19 ノア）黄
 *
 */
import { i18n } from '@/locales'
import { MessageBox, ActionType, MessageBoxOption, MessageBoxType } from '@/components/messageBox/messageBox'

/**
 * メッセージボックスサービス
 *
 * @class
 */
class MsgBoxService {
  /**
   * メッセージボックスを表示する。
   *
   * @param {string} message メッセージ内容
   * @param {confirmCallback} confirmCallback 確認したのコールバック関数
   * @param {MessageBoxOption} options メッセージボックスオプション
   * @returns {void}
   */
  error(message: string, confirmCallback?: () => void, options?: MessageBoxOption): void {
    MessageBox.alert({
      type: MessageBoxType.error,
      message: message,
      title: '',
      showNoButton: false,
      callback: () => {
        if (confirmCallback) {
          confirmCallback()
        }
      },
      ...options
    })
  }

  /**
   * メッセージボックスを表示する。
   *
   * @param {string} message メッセージ内容
   * @param {confirmCallback} confirmCallback 確認したのコールバック関数
   * @param {MessageBoxOption} options メッセージボックスオプション
   * @returns {void}
   */
  info(message: string, confirmCallback?: () => void, options?: MessageBoxOption): void {
    MessageBox.alert({
      message: message,
      title: '',
      showNoButton: false,
      callback: () => {
        if (confirmCallback) {
          confirmCallback()
        }
      },
      ...options
    })
  }

  /**
   * 確認（キャンセルボタンなし）メッセージボックスを表示する。
   *
   * @param {string} message メッセージ内容
   * @param {confirmCallback} confirmCallback 確認したのコールバック関数
   * @param {rejectCallback} rejectCallback 拒絶したのコールバック関数
   * @param {MessageBoxOption} options メッセージボックスオプション
   * @returns {void}
   */
  confirm(message: string, confirmCallback: () => void, rejectCallback?: () => void, options?: MessageBoxOption): void {
    MessageBox.alert({
      message: message,
      title: i18n.t('messages.masterCommon.confirmTitle').toString(),
      showCancelButton: false,
      callback: (action: ActionType) => {
        if (action === ActionType.yes) {
          confirmCallback()
        }

        if (action === ActionType.no && rejectCallback) {
          rejectCallback()
        }
      },
      ...options
    })
  }

  /**
   * 確認（キャンセルボタンあり）メッセージボックスを表示する。
   *
   * @param {string} message メッセージ内容
   * @param {confirmCallback} confirmCallback 確認したのコールバック関数
   * @param {rejectCallback} rejectCallback 拒絶したのコールバック関数
   * @param {cancelCallback} cancelCallback 取消したのコールバック関数
   * @param {MessageBoxOption} options メッセージボックスオプション
   * @returns {void}
   */
  confirmWithCancel(
    message: string,
    confirmCallback: () => void,
    rejectCallback: () => void,
    cancelCallback?: () => void,
    options?: MessageBoxOption
  ): void {
    MessageBox.alert({
      message: message,
      title: i18n.t('messages.masterCommon.confirmTitle').toString(),
      width: '450px',
      showCancelButton: true,
      callback: (action: ActionType) => {
        if (action === ActionType.yes) {
          confirmCallback()
        }

        if (action === ActionType.no) {
          rejectCallback()
        }

        if (action === ActionType.cancel) {
          if (cancelCallback) {
            cancelCallback()
          }
        }
      },
      ...options
    })
  }

  /**
   * 全部メッセージボックスを閉じる。
   *
   * @returns {void}
   */
  closeAll(): void {
    MessageBox.clearQueue()
  }
}

const msgBoxService = new MsgBoxService()
export { msgBoxService }
export * from '@/components/messageBox/messageBox'
